import cn from 'classnames'
import { FC, useCallback, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Checkbox, Switch } from 'core-components/library/checkbox'

import { actualsColor, quotaColor, targetsColor } from '../../config'

import { IS_SHOW_LNF_SWITCHER } from './configs'
import { IDataDisplaySettingsProps } from './models/IDataDisplaySettingsProps'
import { dataDisplaySettingsTranslations as tr } from './translations'

import styles from './DataDisplaySettings.module.scss'

export const DataDisplaySettings: FC<IDataDisplaySettingsProps> = (props) => {
  /** -------------------- States ----------------------- */

  const [isOpen, setOpen] = useState(false)

  /** -------------------- Variables ----------------------- */

  type TMeasures = 'ava' | 'pva' | 'pvp' | 'qva' | 'cvc'

  const { settings, actions, isRetailer, isDistributor } = props

  const isShowPartnerCountControl = useMemo(() => isDistributor, [isDistributor])

  const isShowActualsSplitControl = useMemo(() => !isDistributor, [isDistributor])

  /** -------------------- User actions handlers ----------------------- */

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  const handleYoYChange = useCallback(
    (state: boolean, type: TMeasures) => {
      switch (type) {
        case 'ava':
          actions.setIsShowYoYActualsVsActuals(!state)
          break
        case 'pva':
          actions.setIsShowYoYActualsVsPlans(!state)
          break
        case 'pvp':
          actions.setIsShowYoYPlansVsPlans(!state)
          break
        case 'qva':
          actions.setIsShowYoYQuotaVsActuals(!state)
          break
        case 'cvc':
          actions.setIsShowYoYPartnersVsPartners(!state)
          break
        default:
          break
      }
    },
    [actions]
  )

  const handleQoQChange = useCallback(
    (state: boolean, type: string) => {
      switch (type) {
        case 'ava':
          actions.setIsShowQoQActualsVsActuals(!state)
          break
        case 'pva':
          actions.setIsShowQoQActualsVsPlans(!state)
          break
        case 'pvp':
          actions.setIsShowQoQPlansVsPlans(!state)
          break
        case 'qva':
          actions.setIsShowQoQQuotaVsActuals(!state)
          break
        case 'cvc':
          actions.setIsShowQoQPartnersVsPartners(!state)
          break
        default:
          break
      }
    },
    [actions]
  )

  /** -------------------- Render ----------------------- */

  return (
    <>
      <Button className={cn(styles.openButton, { [styles.show]: isOpen })} onClick={handleOpen}>
        <span className={cn(styles.text, { [styles.show]: isOpen })}>
          <Translation text={tr.dataDisplayButton} span />
        </span>
      </Button>
      <div className={cn(styles.root, { [styles.show]: isOpen })}>
        <div className={cn(styles.header)}>
          <div className={styles.switchersContainer}>
            <div className={styles.switcher}>
              {IS_SHOW_LNF_SWITCHER ? (
                <>
                  <Switch
                    padding
                    checked={settings.isNewLookAndFeel}
                    onChange={(val) => actions.setIsNewLookAndFeel(val)}
                  />
                  <div className={styles.switcherText}>
                    <Translation text={tr.newTableLayoutLookAndFeel} span />
                  </div>
                </>
              ) : (
                <Translation text={tr.dataDisplayHeader} />
              )}
            </div>
          </div>
          <i
            className={cn('kbase kbase-close kbase-sm', styles.closeButton)}
            onClick={handleClose}
          />
        </div>

        <div className={cn(styles.twoColContainer, styles.firstRowColContainer)}>
          <div className={cn(styles.columnContainer)}>
            <div className={styles.buttonContaiter}>
              {!isRetailer && (
                <div className={styles.checkboxContainer} style={{ color: targetsColor }}>
                  <Checkbox
                    label={<Translation text={tr.showHideQuarters} span />}
                    checked={settings.isYearlyPlanning}
                    onChange={(val) => actions.setIsYearlyPlanning(!!val)}
                    disabled={isRetailer}
                  />
                </div>
              )}
              {isRetailer && (
                <div className={styles.checkboxContainer} style={{ color: targetsColor }}>
                  <Checkbox
                    label={<Translation text={tr.showDetailed} span />}
                    checked={settings.isShowDetailedPlanning}
                    onChange={(val) => actions.setIsShowDetailedPlanning(!!val)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={cn(styles.contentContainer)}>
          <div className={cn(styles.twoColContainer)}>
            <div className={cn(styles.columnContainer)}>
              <div className={styles.buttonContaiter}>
                <Translation text={tr.yoy.header} span />
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  onClick={() => handleYoYChange(settings.isShowYoYActualsVsActuals, 'ava')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowYoYActualsVsActuals && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.yoy.ava} span />
                </Button>
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  onClick={() => handleYoYChange(settings.isShowYoYActualsVsPlans, 'pva')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowYoYActualsVsPlans && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.yoy.pva} span />
                </Button>
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  disabled={!settings.isNewLookAndFeel}
                  onClick={() => handleYoYChange(settings.isShowYoYPlansVsPlans, 'pvp')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowYoYPlansVsPlans && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.yoy.pvp} span />
                </Button>
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  disabled={!settings.isNewLookAndFeel || isRetailer}
                  onClick={() => handleYoYChange(settings.isShowYoYQuotaVsActuals, 'qva')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowYoYQuotaVsActuals && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.yoy.qva} span />
                </Button>
              </div>
              {isShowPartnerCountControl && (
                <div className={styles.buttonContaiter}>
                  <Button
                    disabled={!settings.isNewLookAndFeel}
                    onClick={() => handleYoYChange(settings.isShowYoYPartnersVsPartners, 'cvc')}
                    className={cn(styles.button, {
                      [styles.notActive]:
                        !settings.isShowYoYPartnersVsPartners && settings.isNewLookAndFeel,
                    })}
                  >
                    <Translation text={tr.yoy.cvc} span />
                  </Button>
                </div>
              )}
            </div>
            <div className={cn(styles.columnContainer)}>
              <div className={styles.buttonContaiter}>
                <Translation text={tr.qoq.header} span />
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  disabled={!settings.isNewLookAndFeel || settings.isYearlyPlanning}
                  onClick={() => handleQoQChange(settings.isShowQoQActualsVsActuals, 'ava')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowQoQActualsVsActuals && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.qoq.ava} span />
                </Button>
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  disabled={!settings.isNewLookAndFeel || settings.isYearlyPlanning}
                  onClick={() => handleQoQChange(settings.isShowQoQActualsVsPlans, 'pva')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowQoQActualsVsPlans && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.qoq.pva} span />
                </Button>
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  disabled={!settings.isNewLookAndFeel || settings.isYearlyPlanning}
                  onClick={() => handleQoQChange(settings.isShowQoQPlansVsPlans, 'pvp')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowQoQPlansVsPlans && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.qoq.pvp} span />
                </Button>
              </div>
              <div className={styles.buttonContaiter}>
                <Button
                  disabled={!settings.isNewLookAndFeel || settings.isYearlyPlanning}
                  onClick={() => handleQoQChange(settings.isShowQoQQuotaVsActuals, 'qva')}
                  className={cn(styles.button, {
                    [styles.notActive]:
                      !settings.isShowQoQQuotaVsActuals && settings.isNewLookAndFeel,
                  })}
                >
                  <Translation text={tr.qoq.qva} span />
                </Button>
              </div>
              {isShowPartnerCountControl && (
                <div className={styles.buttonContaiter}>
                  <Button
                    disabled={!settings.isNewLookAndFeel || settings.isYearlyPlanning}
                    onClick={() => handleQoQChange(settings.isShowQoQPartnersVsPartners, 'cvc')}
                    className={cn(styles.button, {
                      [styles.notActive]:
                        !settings.isShowQoQPartnersVsPartners && settings.isNewLookAndFeel,
                    })}
                  >
                    <Translation text={tr.qoq.cvc} span />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className={cn(styles.twoColContainer, styles.nextRowColContainer)}>
            <div className={cn(styles.columnContainer)}>
              <div className={styles.buttonContaiter}>
                <div className={styles.checkboxContainer}>
                  <Translation text={tr.achievements.header} span />
                </div>
                <div className={styles.checkboxContainer} style={{ color: targetsColor }}>
                  <Checkbox
                    disabled={!settings.isNewLookAndFeel}
                    label={<Translation text={tr.achievements.plan} span />}
                    checked={settings.isShowPlanAchievement && settings.isNewLookAndFeel}
                    onChange={(val) => actions.setIsShowPlanAchievement(!!val)}
                  />
                </div>
                <div className={styles.checkboxContainer} style={{ color: quotaColor }}>
                  <Checkbox
                    disabled={!settings.isNewLookAndFeel || isRetailer}
                    label={<Translation text={tr.achievements.quota} span />}
                    checked={settings.isShowQuotaAchievement && settings.isNewLookAndFeel}
                    onChange={(val) => actions.setIsShowQuotaAchievement(!!val)}
                  />
                </div>
              </div>
            </div>
            <div className={cn(styles.columnContainer)}>
              <div className={styles.buttonContaiter}>
                <div className={styles.checkboxContainer}>
                  <Translation text={tr.additions.header} span />
                </div>
                <div className={styles.checkboxContainer} style={{ color: quotaColor }}>
                  <Checkbox
                    disabled={isRetailer}
                    label={<Translation text={tr.additions.quotaTargets} span />}
                    checked={settings.isShowQuotaTargets}
                    onChange={(val) => actions.setIsShowQuotaTargets(!!val)}
                  />
                </div>
                {isShowActualsSplitControl && (
                  <div className={styles.checkboxContainer} style={{ color: actualsColor }}>
                    <Checkbox
                      label={<Translation text={tr.additions.actualsSplit} span />}
                      checked={settings.isShowActualsSplit}
                      onChange={(val) => actions.setIsShowActualsSplit(!!val)}
                    />
                  </div>
                )}
                {!isDistributor && (
                  <div className={styles.checkboxContainer} style={{ color: actualsColor }}>
                    <Checkbox
                      label={<Translation text={tr.additions.smbVsEuSplit} span />}
                      checked={settings.isShowSmbVsEuSplit}
                      onChange={(val) => actions.setIsShowSmbVsEuSplit(!!val)}
                    />
                  </div>
                )}
                {isShowPartnerCountControl && (
                  <div className={styles.checkboxContainer} style={{ color: targetsColor }}>
                    <Checkbox
                      label={<Translation text={tr.additions.partnerCount} span />}
                      checked={settings.isShowPartnerCount}
                      onChange={(val) => actions.setIsShowPartnerCount(!!val)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
